import React, {FC, useState, useEffect} from "react"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Button from "@irati/ui/src/Button/Button"
import {Card} from "@irati/ui/src/Layout"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Form from "@irati/ui/src/Form"
import adminTracking from "@irati-tracking/admin"

import {Img} from "../styled"
import {StyledCheckbox} from "./styled"
import AbandonModal from "./AbandonModal"

interface Props extends InjectIntl {
  show: boolean
  onClose: () => void
  onNext: () => void
}

const IntegrationChecklist: FC<Props> = ({show, onClose, onNext, formatMessage}) => {
  const [checklist, setChecklist] = useState(false)
  const [alertError, setAlertError] = useState(false)
  const [showModal, setShowModal] = useState(show)
  const [showAbandonModal, setShowAbandonModal] = useState(false)
  const theme = useTheme()

  const handleOnClick = () => {
    if (!checklist) {
      setAlertError(true)
    } else {
      adminTracking.hrisUpsellSetup("admin_dysnc_set_up_before_get_started", {action: "before_get_started_complete"})
      onClose()
      onNext()
    }
  }

  const handleOnChange = () => {
    setChecklist(!checklist)
    setAlertError(false)
  }

  const handleOnReturn = () => {
    setShowAbandonModal(false)
    setShowModal(true)
  }

  const handleOnClose = () => {
    setShowAbandonModal(false)
    onClose()
  }

  const handleOnHide = () => {
    setShowModal(false)
    setShowAbandonModal(true)
  }

  useEffect(() => {
    setShowModal(show)
    if (!show) {
      setChecklist(false)
      setAlertError(false)
    }
  }, [show])

  return (
    <>
      <GenericModal customWidth={800} hideActions show={showModal} onHide={handleOnHide}>
        <H2 marginBottom>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.title" />
        </H2>
        {alertError && (
          <Alert
            type={AlertTypes.error}
            title="admin.components.integration.directorySync.hris.upsell.integrationChecklist.validationChecklist"
          />
        )}
        <Body marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.subTitle" />
        </Body>
        <Flex flex="1 1 50%" gap={3} marginBottom={2}>
          <Card>
            <Flex justifyContent="center" marginBottom>
              <Img
                medium
                src={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.integrationChecklist.item1.img"})}
              />
            </Flex>
            <Body small color={theme.colors.greys.black}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.item1.text" />
            </Body>
          </Card>
          <Card>
            <Flex justifyContent="center" marginBottom>
              <Img
                medium
                src={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.integrationChecklist.item2.img"})}
              />
            </Flex>
            <Body small color={theme.colors.greys.black}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.item2.text" />
            </Body>
          </Card>
        </Flex>
        <Flex flex="1 1 50%" gap={3} marginBottom={2}>
          <Card>
            <Flex justifyContent="center" marginBottom>
              <Img
                medium
                src={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.integrationChecklist.item3.img"})}
              />
            </Flex>
            <Body small color={theme.colors.greys.black}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.item3.text" />
            </Body>
          </Card>
          <Card>
            <Flex justifyContent="center" marginBottom>
              <Img
                medium
                src={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.integrationChecklist.item4.img"})}
              />
            </Flex>
            <Body small color={theme.colors.greys.black}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.item4.text" />
            </Body>
          </Card>
        </Flex>
        <Form>
          <StyledCheckbox name="integrationChecklist" checked={checklist} onChange={handleOnChange}>
            <Body color={theme.colors.greys.black} display="inline-block">
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.checkboxLabel" />
            </Body>
          </StyledCheckbox>
        </Form>
        <Flex justifyContent="flex-end">
          <Flex width="250px">
            <Button full primary onClick={handleOnClick}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.integrationChecklist.button" />
            </Button>
          </Flex>
        </Flex>
      </GenericModal>
      <AbandonModal show={showAbandonModal} onClose={handleOnClose} onReturn={handleOnReturn} />
    </>
  )
}

export default injectIntl(IntegrationChecklist)
